/**
 * DisclaimerScreen Component
 * 
 * Displays a loading screen with legal disclaimers after authentication.
 * Shows for 20 seconds before automatically dismissing.
 * Uses centered waveform animation with card below for disclaimer text.
 */

import React, { useEffect, useState } from 'react';
import { waveform } from 'ldrs';
import { useTranslation } from '../hooks/useTranslation';
import type { MapLanguage } from '../types';
import styles from './DisclaimerScreen.module.css';

// Register the waveform loader
waveform.register();

/**
 * DisclaimerScreen component props
 */
export interface DisclaimerScreenProps {
  /** Current language */
  language: MapLanguage;
  /** Callback when disclaimer screen completes */
  onComplete: () => void;
}

/**
 * DisclaimerScreen Component
 * 
 * Shows a professional loading screen with legal disclaimers.
 * Automatically dismisses after 20 seconds.
 * Loading animation centered on screen, disclaimer card slides up from bottom.
 */
export const DisclaimerScreen: React.FC<DisclaimerScreenProps> = ({
  language,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [isCardVisible, setIsCardVisible] = useState(false);
  const [isBlurVisible, setIsBlurVisible] = useState(true);

  // Slide card up on mount
  useEffect(() => {
    const slideTimer = setTimeout(() => {
      setIsCardVisible(true);
    }, 100);

    return () => clearTimeout(slideTimer);
  }, []);

  // Auto-dismiss after 20 seconds
  useEffect(() => {
    // Set timer for 20 seconds
    const timer = setTimeout(() => {
      // Start slide down animation and fade out blur
      setIsCardVisible(false);
      setIsBlurVisible(false);
      
      // Wait for slide animation to complete (500ms) before calling onComplete
      setTimeout(() => {
        onComplete();
      }, 500);
    }, 20000);

    // Cleanup timer on unmount
    return () => {
      clearTimeout(timer);
    };
  }, [onComplete]);

  // Check if language is Chinese (to show personalStance)
  const isChinese = language === 'zh-Hans' || language === 'zh-Hant';

  return (
    <>
      {/* Blur overlay for globe background */}
      <div 
        className={styles.blurOverlay} 
        style={{ opacity: isBlurVisible ? 1 : 0 }}
      />

      {/* Disclaimer Card - Slides up from bottom with loading at top */}
      <div className={`${styles.card} ${isCardVisible ? styles.cardVisible : ''}`}>
        {/* Loading text at top of card */}
        <div className={styles.loadingSection}>
          <div className={styles.loadingText}>
            {t.disclaimer.loading.replace('...', '')}
            <span className={styles.loadingDots}></span>
          </div>
        </div>

        {/* Important Disclaimer Title - Left aligned, translated */}
        <h1 className={styles.title}>{t.disclaimer.title}</h1>

        {/* Disclaimer Content */}
        <div className={styles.disclaimerContent}>
          <p className={styles.disclaimerParagraph}>{t.disclaimer.content}</p>
          
          {/* Show personal stance paragraph only for Chinese languages */}
          {isChinese && t.disclaimer.personalStance && (
            <p className={styles.disclaimerParagraph}>{t.disclaimer.personalStance}</p>
          )}
        </div>

        {/* Consent Statement */}
        <div className={styles.consent}>{t.disclaimer.consent}</div>
      </div>
    </>
  );
};

